<template>
  <ul v-if="renderList.length" class="app-search-result">
    <li
      v-for="(item, index) in renderList"
      :key="index"
      class="app-search-result-item"
      @click="$emit('visit', item.title)"
    >
      <NuxtLink :to="formatUrl(item)" :target="!isGame ? '_blank' : '_self'">
        <div class="app-search-result-icon">
          <IconsDocs v-if="Number(item.article_type) === 1" class="h-4 w-4" />
          <IconsTutorial v-else class="h-4 w-4" />
        </div>
        <div class="app-search-result-content">
          <div class="app-search-result-title ..." v-html="item.title" />
          <div class="app-search-result-ctx ..." v-html="formatHtml(item.content)" />
          <div class="app-search-result-attribution ...">{{ item.attribution }}</div>
        </div>
      </NuxtLink>
    </li>
  </ul>
  <div v-else class="app-search-empty">
    <img class="app-search-empty-img" src="@/assets/images/search_empty.png" alt="" />
    <span class="app-search-empty-text">{{ formatTransify('UGC_OFFICIAL_WEBSITE_SEARCH_NORESULTS') }}</span>
  </div>
</template>
<script lang="ts" setup>
import { SearchGlobalData } from '~/server/types'

const props = defineProps({
  page: {
    type: Number,
    default: 1
  },
  pageCounts: {
    type: Number,
    default: 25
  },
  data: {
    type: Array as PropType<SearchGlobalData[]>,
    default: () => []
  },
  currentCategory: {
    type: Number,
    default: 0
  },
  isGame: Boolean
})
const store = useStore()
const { formatTransify } = store
const { config } = storeToRefs(store)
defineEmits(['close', 'visit'])
const { $markdown } = useNuxtApp()
const renderList = computed(() => {
  const startIndex = (props.page - 1) * props.pageCounts
  const endIndex = startIndex + props.pageCounts
  const currentList =
    props.currentCategory === 0
      ? props.data
      : props.data.filter((item) => Number(item.article_type) === props.currentCategory)
  return currentList.slice(0, endIndex)
})
function formatMarkdown(content: string) {
  return content
    ?.replace(/```([\s\S]*?)```/g, function (_, p1) {
      return p1
    }) // markdown-it 不支持加粗、斜体、斜体加粗的结束符前有特殊字符，会导致解析错误，为避免此类情况，判断此类语法直接转化成html
    .replace(/(\*\*\*|\*\*|\*)(.*?)(\*\*\*|\*\*|\*)/g, function (_: any, _p1: string, p2) {
      return `${p2}`
    })
    .replace(/<(?!\/?em)[^<>]*>/g, '')
}
function formatHtml(content: string) {
  try {
    // 如果 markdown 未初始化，返回原始文本
    const html = $markdown.render(formatMarkdown(content) || '')
    return html ? html.replace(/<(?!\/?em)[^<>]*>/g, '') : ''
  } catch (error) {
    console.warn('Search result markdown render failed:', error)
    return formatMarkdown(content) || ''
  }
}
function formatUrl(result: SearchGlobalData) {
  const { p4_id: p4, article_type: type, lang_id: langId, platform } = result
  const lang = config.value.lang_list.find((item) => item.id === Number(langId))?.lang || 'en'
  switch (Number(platform)) {
    case -1:
      return `/${lang}/docs/${p4}/`
    case 1:
      return `/${lang}/tutorial/fe/${p4}/`
    case 0:
      return `/${lang}/tutorial/cl/${p4}/`
    default:
      return `/${lang}/`
  }
}
</script>
<style lang="scss">
.app-search-result-item em {
  @apply font-bold not-italic text-primary;
}
/* 如果有些标签没有被正则过滤掉，则限制展示，比如：img标签的src包含了关键词可能会导致没有被过滤*/
.app-search-result-ctx {
  img,
  video,
  iframe,
  audio {
    @apply hidden;
  }
}
</style>
<style scoped lang="scss">
.app-search-result {
  @apply cursor-pointer text-white;
}

.app-search-result-item {
  @apply relative mb-2 rounded-sm px-2 py-1  last:mb-0 lg:hover:bg-dark-background;
}

.app-search-result-icon {
  @apply absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-dark-background text-light;
  @apply rtl:left-auto rtl:right-2;
}

.app-search-result-content {
  @apply ltr:ml-12 rtl:mr-12;
}

.app-search-result-title {
  @apply w-full truncate font-medium text-white text-base max-lg:text-sm;
}

.app-search-result-attribution {
  @apply w-full truncate text-xs leading-5 text-[#707070] max-lg:text-m-p;
}

.app-search-result-ctx {
  @apply w-full truncate text-sm leading-5.5 text-light max-lg:text-xs max-lg:leading-4.5;
}

.app-search-empty {
  @apply absolute left-1/2 top-[35%] flex -translate-x-1/2 w-[90%] flex-col items-center max-w-full;
}

.app-search-empty-img {
  @apply h-[94px] w-28;
}

.app-search-empty-text {
  @apply w-[80%] mt-1 text-light text-base text-center;
}
</style>
